import { Link } from "react-router-dom";
import type { CanonicalWork } from "../generated/contracts";
import { useLibrary } from "../contexts/LibraryContext";
import { usePlayer } from "../contexts/PlayerContext";
import { Cover } from "./Cover";

import { Icon } from "./Icon";

function duration(value?: number | null) {
  if (!value) return null;
  const minutes = Math.round(value / 60);
  return minutes >= 60
    ? `${Math.floor(minutes / 60)}h ${minutes % 60}m`
    : `${minutes} min`;
}

export function WorkCard({
  work,
  reason,
  compact = false,
  artwork,
  featured = false,
}: {
  work: CanonicalWork;
  reason?: string | null;
  compact?: boolean;
  artwork?: string;
  featured?: boolean;
}) {
  const { isFavorite, toggleFavorite } = useLibrary();
  const player = usePlayer();
  const playable = player.playableAsset(work);
  return (
    <article className={`work-card ${compact ? "work-card--compact" : ""} ${featured ? "work-card--featured" : ""}`}>
      <Link
        className="work-card__cover-link"
        to={`/works/${work.slug}`}
        aria-label={`Open ${work.title}`}
      >
        <Cover
          work={work}
          size={compact ? "mini" : "card"}
          artworkOverride={artwork}
        />
      </Link>
      <div className="work-card__body">
        <div className="work-card__eyebrow">
          {featured ? work.kind.replaceAll("-", " ") : work.editorialStatus === "contemporary-adaptation"
            ? "Contemporary adaptation"
            : work.editorialStatus === "contemporary-original"
              ? "New from Eternally Blessed"
              : work.series?.title || work.kind.replaceAll("-", " ")}
          {!featured && work.year ? ` · ${work.year}` : ""}
        </div>
        <Link className="work-card__title" to={`/works/${work.slug}`}>
          {work.title}
        </Link>
        <div className="work-card__meta">
          {work.contributors.map((item) => item.name).join(", ") ||
            "Eternally Blessed archive"}
          {!featured && duration(work.durationSeconds)
            ? ` · ${!featured && duration(work.durationSeconds)}`
            : ""}
        </div>
        {reason ? (
          <div className="work-card__reason">Matched {reason}</div>
        ) : null}
        <div className="work-card__actions">
          {!playable && !compact && !featured ? (
            <Link className="round-action" to={`/works/${work.slug}`}>
              {!!work.bodyExcerpt ||
              work.assets.some((a) => ["pdf", "ebook", "text"].includes(a.kind))
                ? "Read →"
                : work.assets.some((a) => a.kind === "video")
                  ? "Watch →"
                  : "View details →"}
            </Link>
          ) : null}
          {playable ? (
            <button
              type="button"
              className="round-action round-action--play"
              onClick={() => player.load(work)}
              aria-label={`Play ${work.title}`}
            >
              <Icon name="play" />
              <span>Listen</span>
            </button>
          ) : null}
          <button
            type="button"
            className="round-action save-action"
            aria-pressed={isFavorite(work.id)}
            title={isFavorite(work.id) ? "Remove from saved" : "Save for later"}
            onClick={() => toggleFavorite(work)}
            aria-label={`${isFavorite(work.id) ? "Remove" : "Save"} ${work.title}`}
          >
            <Icon name="save" />
          </button>
        </div>
      </div>
    </article>
  );
}
